import { createTheme } from '@mui/material/styles';
import { colors, fonts, radius, shadows, easing } from './betravelTokens';

export const betravelTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary,
      dark: colors.primaryDark,
      light: colors.primaryLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.ink,
      dark: '#000000',
      light: colors.inkSoft,
      contrastText: '#ffffff',
    },
    success: { main: colors.success, dark: colors.successDark, light: colors.successLight },
    error: { main: colors.error, light: colors.errorLight },
    warning: { main: colors.warning, light: colors.warningLight },
    info: { main: colors.info },
    background: {
      default: colors.bgCream,
      paper: colors.bg,
    },
    text: {
      primary: colors.ink,
      secondary: colors.text,
      disabled: colors.muted,
    },
    divider: colors.line,
  },
  typography: {
    fontFamily: fonts.body,
    h1: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em' },
    h3: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontFamily: fonts.display, fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontFamily: fonts.display, fontWeight: 600 },
    h6: { fontFamily: fonts.display, fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.02em' },
    body1: { color: colors.text, lineHeight: 1.7 },
    body2: { color: colors.text, lineHeight: 1.6 },
  },
  shape: {
    borderRadius: radius.md,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.bgCream,
          fontFamily: fonts.body,
          color: colors.ink,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        'h1, h2, h3, h4, h5, h6': {
          fontFamily: fonts.display,
          color: colors.ink,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.02em',
          paddingLeft: 22,
          paddingRight: 22,
          transition: `all 0.3s ${easing}`,
        },
        contained: {
          boxShadow: '0 6px 18px rgba(216, 157, 47, 0.25)',
          '&:hover': {
            boxShadow: '0 10px 24px rgba(216, 157, 47, 0.35)',
            transform: 'translateY(-1px)',
          },
        },
        containedPrimary: {
          background: colors.primary,
          '&:hover': {
            background: colors.primaryDark,
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': { borderWidth: 2 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          boxShadow: shadows.md,
          border: `1px solid ${colors.line}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: radius.md },
        elevation1: { boxShadow: shadows.sm },
        elevation2: { boxShadow: shadows.md },
        elevation3: { boxShadow: shadows.md },
        elevation4: { boxShadow: shadows.lg },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: colors.ink,
          boxShadow: shadows.sm,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.md,
            '& fieldset': { borderColor: colors.line },
            '&:hover fieldset': { borderColor: colors.primaryLight },
            '&.Mui-focused fieldset': { borderColor: colors.primary },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: '0.02em',
        },
        colorPrimary: {
          backgroundColor: colors.primarySoft,
          color: colors.primaryDark,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: radius.md,
          fontWeight: 500,
          '&.Mui-selected': {
            backgroundColor: colors.primary,
            color: '#ffffff',
            '&:hover': { backgroundColor: colors.primaryDark },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `all 0.2s ${easing}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.line },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.primary,
          '&:hover': { color: colors.primaryDark },
        },
      },
    },
  },
});
